import { makeAutoObservable } from 'mobx';
import { fromPromise, type IPromiseBasedObservable } from 'mobx-utils';
import type PulseApi from '../../../api/PulseApi';
import type AuthStore from '../../login/store/AuthStore';
import type Products from '../../products/store/Products';
import type ProductStore from '../../products/store/ProductStore';
import Bundle, { type BundleConstructorDto, type BundleDto } from './Bundle';
import Bundles from './Bundles';

export default class BundleStore {
  constructor(
    private readonly api: PulseApi,
    private readonly productStore: ProductStore,
    private readonly authStore: AuthStore,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  fetchBundleAndProducts(bundleId: string): IPromiseBasedObservable<{
    bundle: Bundle;
    products: Products;
  }> {
    return fromPromise(
      Promise.all([
        this.api.bundles
          .getBundle(bundleId)
          .then((dto) => this.unSerialize(dto)),
        this.productStore.fetchAllProducts(),
      ]).then(([bundle, products]) => ({
        bundle,
        products: products.cloneFiltered((p) => !p.archived),
      })),
    );
  }

  fetchAllBundles(persistentFilter = false): IPromiseBasedObservable<Bundles> {
    return fromPromise(
      this.api.bundles.getAllBundles().then((dtos) => {
        return new Bundles(dtos, this.saveBundle, persistentFilter);
      }),
    );
  }

  createBundle(initialFields: BundleDto = {}): Bundle {
    return this.unSerialize({ ...initialFields, savingAction: 'create' });
  }

  async saveBundle(bundle: Bundle) {
    if (bundle.savingAction === 'archive') {
      await this.api.bundles.archiveBundle(bundle.id);
      return;
    }

    if (bundle.savingAction === 'create') {
      const dto = await this.api.bundles.createBundle(bundle.toJSON());
      bundle.updateAfterCreation(dto);
      return;
    }

    await this.api.bundles.updateBundle(bundle.toJSON());
  }

  async deleteBundle(bundle: Bundle) {
    return this.api.bundles.archiveBundle(bundle.id);
  }

  private unSerialize(dto: BundleConstructorDto): Bundle {
    const forceEditMode = this.authStore.userHasRole('ADMIN', { mode: 'main' });

    return new Bundle({ forceEditMode, ...dto }, this.saveBundle);
  }
}
